import React, { useMemo, useRef, useState } from 'react';
import { useProducts } from '../useProducts';
import { formatProductTitle } from '../formatProductTitle';
import { productGalleryImagePaths, customerUploadMediaUrl } from '../productMediaUtils';
import { uploadAdminImage } from './adminMediaUpload';

function previewSrc(value) {
  const v = String(value || '').trim();
  if (!v) return '';
  return customerUploadMediaUrl(v);
}

function CatalogImagePicker({ onPick, onClose }) {
  const { allProducts } = useProducts();
  const [q, setQ] = useState('');

  const rows = useMemo(() => {
    const needle = q.trim().toLowerCase();
    const out = [];
    for (const p of allProducts || []) {
      const paths = productGalleryImagePaths(p);
      const image = paths[0] || String(p?.image || '').trim();
      if (!image) continue;
      const name = formatProductTitle(p.name || '');
      if (needle && !name.toLowerCase().includes(needle) && !String(p.category || '').toLowerCase().includes(needle)) {
        continue;
      }
      out.push({
        key: `${p.category}-${p.id}`,
        name,
        category: p.category,
        image,
      });
      if (out.length >= 40) break;
    }
    return out;
  }, [allProducts, q]);

  return (
    <div className="admin-site-image-field__catalog">
      <div className="d-flex flex-wrap gap-2 align-items-center mb-2">
        <input
          type="search"
          className="form-control form-control-sm flex-grow-1"
          placeholder="Search products…"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          aria-label="Search catalog images"
        />
        <button type="button" className="btn btn-sm btn-outline-secondary" onClick={onClose}>
          Close
        </button>
      </div>
      {rows.length === 0 ? (
        <p className="small text-muted mb-0">No catalog images found.</p>
      ) : (
        <ul className="admin-site-image-field__catalog-grid list-unstyled mb-0">
          {rows.map((row) => (
            <li key={row.key}>
              <button
                type="button"
                className="admin-site-image-field__catalog-item"
                onClick={() => onPick(row.image)}
                title={row.name}
              >
                <img src={previewSrc(row.image)} alt="" loading="lazy" />
                <span className="admin-site-image-field__catalog-caption">{row.name}</span>
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

/**
 * Shopify-style hero / OG image field — preview, upload, catalog pick, optional path.
 */
export default function AdminSiteImageField({
  id,
  label,
  value,
  onChange,
  hint,
  sessionToken,
  aspect = '4 / 5',
  accept = 'image/jpeg,image/png,image/webp,image/avif,image/gif',
  onPersist,
}) {
  const fileRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [persisting, setPersisting] = useState(false);
  const [error, setError] = useState('');
  const [showCatalog, setShowCatalog] = useState(false);
  const [showPath, setShowPath] = useState(false);

  const src = previewSrc(value);
  const hasSession = Boolean(String(sessionToken || '').trim());
  const busy = uploading || persisting;

  const applyImage = async (path) => {
    onChange(path);
    setShowCatalog(false);
    setError('');
    if (!onPersist) return;
    setPersisting(true);
    try {
      const ok = await onPersist(path);
      if (ok === false) {
        setError('Upload worked but saving site settings failed. Click Save at the top.');
      }
    } catch (err) {
      setError(err.message || 'Could not save to site settings.');
    } finally {
      setPersisting(false);
    }
  };

  const onFileChange = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    if (!hasSession) {
      setError('Sign in under Admin to upload images to the server.');
      return;
    }
    setUploading(true);
    setError('');
    try {
      const path = await uploadAdminImage(sessionToken, file);
      await applyImage(path);
    } catch (err) {
      setError(err.message || 'Upload failed.');
    } finally {
      setUploading(false);
    }
  };

  const onClear = async () => {
    onChange('');
    setError('');
    setShowCatalog(false);
    if (onPersist) {
      setPersisting(true);
      try {
        await onPersist('');
      } finally {
        setPersisting(false);
      }
    }
  };

  return (
    <div className="col-12 admin-site-image-field">
      <label className="form-label" htmlFor={id}>
        {label}
      </label>

      <div
        className={`admin-site-image-field__preview${src ? ' admin-site-image-field__preview--filled' : ''}`}
        style={{ aspectRatio: aspect }}
      >
        {src ? (
          <img src={src} alt="" className="admin-site-image-field__img" />
        ) : (
          <div className="admin-site-image-field__placeholder">
            <span>No image selected</span>
            <span className="small text-muted">Upload or pick from catalog</span>
          </div>
        )}
      </div>

      <div className="admin-site-image-field__actions d-flex flex-wrap gap-2 mt-2">
        <button
          type="button"
          className="btn btn-sm btn-primary"
          disabled={busy || !hasSession}
          onClick={() => fileRef.current?.click()}
        >
          {uploading ? 'Uploading…' : persisting ? 'Saving…' : 'Upload image'}
        </button>
        <button
          type="button"
          className="btn btn-sm btn-outline-secondary"
          onClick={() => setShowCatalog((v) => !v)}
        >
          {showCatalog ? 'Hide catalog' : 'Choose from catalog'}
        </button>
        {value ? (
          <button type="button" className="btn btn-sm btn-outline-danger" onClick={onClear}>
            Remove
          </button>
        ) : null}
        <button
          type="button"
          className="btn btn-sm btn-link text-muted px-1"
          onClick={() => setShowPath((v) => !v)}
        >
          {showPath ? 'Hide path' : 'Edit path'}
        </button>
      </div>

      <input
        ref={fileRef}
        id={`${id}-file`}
        type="file"
        accept={accept}
        className="visually-hidden"
        onChange={onFileChange}
      />

      {!hasSession ? (
        <p className="small text-warning mb-0 mt-2">Sign in under Admin to enable server upload.</p>
      ) : null}

      {showCatalog ? (
        <CatalogImagePicker
          onPick={(path) => applyImage(path)}
          onClose={() => setShowCatalog(false)}
        />
      ) : null}

      {showPath ? (
        <div className="mt-2">
          <input
            id={id}
            className="form-control form-control-sm font-monospace"
            value={value || ''}
            onChange={(e) => onChange(e.target.value)}
            placeholder="/serve_upload.php?f=… or https://…"
          />
        </div>
      ) : null}

      {hint ? <div className="form-text mt-2">{hint}</div> : null}
      {error ? (
        <div className="small text-danger mt-1" role="alert">
          {error}
        </div>
      ) : null}
    </div>
  );
}
